import React, { useEffect, useRef, useState } from 'react';
import { InputLengthStyleProps, TextFieldProps } from './TextField.types';
import { setValidInputDimension } from '../utils/validateStyleDimension';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { useMergedRef } from '@mantine/hooks';

const errorClasses = 'tw:border-sq-danger';

const borderColorClasses = [
  'tw:border-sq-disabled-gray',
  'tw:dark:border-sq-dark-disabled-gray',
  'tw:hover:border-sq-theme-dark',
  'tw:dark:hover:border-sq-theme-dark-dark',
  'tw:dark:focus:border-sq-theme-dark-dark',
  'tw:dark:active:border-sq-theme-darker',
  'tw:focus:border-sq-theme-dark',
  'tw:active:border-sq-theme-dark',
].join(' ');

const baseClasses =
  'tw:h-8.5 tw:leading-normal tw:outline-none tw:py-1 tw:px-3' +
  ' tw:disabled:pointer-events-none tw:disabled:bg-sq-light-gray tw:disabled:dark:bg-sq-dark-disabled-gray' +
  ' tw:disabled:cursor-not-allowed tw:p-1 tw:border-solid tw:border tw:placeholder-gray-400!' +
  ' tw:dark:placeholder-sq-dark-text/30! specTextField';

const darkTheme = 'tw:dark:bg-sq-dark-background tw:dark:text-sq-dark-text tw:disabled:dark:text-sq-dark-text/30';
const lightTheme = 'tw:text-sq-text-color tw:disabled:text-sq-darkish-gray';

const sizeClasses = {
  sm: 'tw:text-sm',
  lg: 'tw:text-xl',
};
/**
 * Textfield.
 */
export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps & InputLengthStyleProps>((props, ref) => {
  const {
    readonly = false,
    disabled = false,
    onChange,
    onPaste,
    onKeyUp,
    onFocus,
    onBlur,
    onKeyDown,
    id,
    name,
    size = 'sm',
    value,
    placeholder,
    extraClassNames,
    testId,
    type = 'text',
    inputGroup,
    step,
    showError,
    errorText,
    min,
    max,
    maxLength,
    minLength,
    required = false,
    autoComplete = 'off',
    inputWidth = undefined,
    inputHeight = undefined,
    autoFocus = false,
    ...tooltipProps
  } = props;

  const internalRef = useRef<HTMLInputElement | null>(null);
  const mergedRef = useMergedRef(ref, internalRef);
  const [cursor, setCursor] = useState(null);
  const tooltipData = getQTipData(tooltipProps);

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        internalRef.current?.focus();
      }, 0);
    }
  }, []);

  useEffect(() => {
    const input = internalRef.current! as unknown as HTMLInputElement;
    if (input && type !== 'number' && type !== 'email') input.setSelectionRange(cursor, cursor);
  }, [ref, cursor, value]);

  const handleChange = (e: any) => {
    setCursor(e.target.selectionStart);
    onChange && onChange(e);
  };

  useEffect(() => {
    /**
     * we need to change the value only if it's different since the internal state of "input" will change it anyway
     * this will only be the case when the value has been changed externally via store (undo / redo)
     */
    if (value !== null && value !== undefined && value !== internalRef.current?.value && internalRef.current) {
      // we need to use this method because using the value props directly will switch the input to a "controlled"
      // component
      internalRef.current.value = `${value}`;
    }
  }, [value]);

  let borderRadius = 'tw:rounded-md';
  if (inputGroup === 'left') {
    borderRadius = 'tw:rounded-l-md tw:border-r-0 tw:focus:border-r' + ' tw:active:border-r';
  } else if (inputGroup === 'right') {
    borderRadius = 'tw:rounded-r-md tw:border-l-0 tw:focus:border-l tw:active:border-l';
  }

  const appliedClasses = `${baseClasses} ${
    sizeClasses[size]
  } ${extraClassNames} ${lightTheme} ${darkTheme} ${borderRadius} ${showError ? errorClasses : borderColorClasses} `;

  const inputProp = setValidInputDimension(inputWidth, inputHeight)
    ? {
        style: setValidInputDimension(inputWidth, inputHeight),
      }
    : {};
  const inputLenghtProp: InputLengthStyleProps = {};
  if (maxLength) inputLenghtProp.maxLength = maxLength;
  if (minLength) inputLenghtProp.minLength = minLength;

  return (
    <>
      <input
        ref={mergedRef}
        data-testid={testId}
        name={name}
        id={id}
        type={type}
        value={value}
        className={appliedClasses}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        autoComplete={autoComplete}
        onChange={handleChange}
        onPaste={onPaste}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        step={step}
        required={required}
        min={min}
        max={max}
        {...inputLenghtProp}
        {...inputProp}
        {...tooltipData}
      />
      {errorText && showError && <div className="tw:text-sq-danger tw:text-xs tw:mt-1">{errorText}</div>}
    </>
  );
});
